import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { Count } from 'Src/models/count';
import { superAdminDelete, superAdminGet, superAdminPost, superAdminPut } from './api';

export interface SuperAdminStudentsFilter {
  limit?: number;
  skip?: number;
  order?: string;
  where?: any;
}

export interface SuperAdminStudentNextOfKin {
  relationship?: string;
  firstName?: string;
  lastName?: string;
  phones?: string[];
  priority?: number;
  email?: string;
}

export interface SuperAdminStudentData {
  firstName?: string;
  lastName?: string;
  birthday?: string;
  gender?: string;
  avatar?: string;
  formId?: string;
  houseId?: string;
  medicalInfo?: string;
  ethnicityCode?: string;
  specialEducationNeeds?: boolean;
  pupilsPremium?: boolean;
  freeSchoolMeals?: boolean;
  misId?: string;
  misEmail?: string;
  misPhone?: string;
  nextOfKin?: SuperAdminStudentNextOfKin[];
}

export interface SuperAdminStudentActivateData {
  studentId: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

const DEFAULT_FILTER: SuperAdminStudentsFilter = {
  limit: LIMIT,
  skip: DEFAULT_SKIP,
  order: 'lastName ASC'
};

function buildFilter(filter: SuperAdminStudentsFilter = DEFAULT_FILTER): SuperAdminStudentsFilter {
  return {
    where: filter.where || {},
    skip: typeof filter.skip === 'number' ? filter.skip : DEFAULT_FILTER.skip,
    limit: typeof filter.limit === 'number' ? filter.limit : DEFAULT_FILTER.limit,
    order: filter.order || DEFAULT_FILTER.order
  };
}

export function getSuperAdminSchoolStudents(
  schoolId: string,
  filter: SuperAdminStudentsFilter = DEFAULT_FILTER
): Promise<any[]> {
  return superAdminGet<any[]>(`/schools/${schoolId}/students`, {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function getAllSuperAdminSchoolStudents(schoolId: string, where?: any): Promise<any[]> {
  return superAdminGet<any[]>(`/schools/${schoolId}/students`, {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: DEFAULT_FILTER.order
      }
    }
  });
}

export function getSuperAdminSchoolStudentsCount(schoolId: string, where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>(`/schools/${schoolId}/students/count`, config);
}

export function getSuperAdminSchoolStudent(schoolId: string, studentId: string): Promise<any> {
  return superAdminGet<any>(`/schools/${schoolId}/students/${studentId}`);
}

export function createSuperAdminSchoolStudent(schoolId: string, data: SuperAdminStudentData): Promise<any> {
  return superAdminPost<any>(`/schools/${schoolId}/students`, data);
}

export function updateSuperAdminSchoolStudent(
  schoolId: string,
  studentId: string,
  data: SuperAdminStudentData
): Promise<any> {
  return superAdminPut<any>(`/schools/${schoolId}/students/${studentId}`, data);
}

export function deleteSuperAdminSchoolStudent(schoolId: string, studentId: string): Promise<void> {
  return superAdminDelete<void>(`/schools/${schoolId}/students/${studentId}`);
}

export function mergeSuperAdminSchoolStudent(
  schoolId: string,
  studentWithGamesId: string,
  studentWithoutGamesId: string,
  permissionId?: string
): Promise<any> {
  const data: any = {
    userId: studentWithoutGamesId
  };

  if (permissionId) {
    data.permissionId = permissionId;
  }

  return superAdminPost<any>(`/schools/${schoolId}/students/${studentWithGamesId}/merge`, data);
}

export function activateSuperAdminSchoolStudents(
  schoolId: string,
  students: SuperAdminStudentActivateData[]
): Promise<any> {
  return superAdminPost<any>(`/schools/${schoolId}/students/activate`, { students });
}
